import axios from "axios";
import React, { createContext, useContext, useEffect, useState } from "react";
import apiPath from "../constants/apiPath";
import { Severty, ShowToast } from "../helper/toast";

export const AuthContext = createContext();

const getStoredToken = () => {
  const directToken = localStorage.getItem("token");
  if (directToken) return directToken;

  try {
    const loginDataRaw = localStorage.getItem("loginData");
    if (!loginDataRaw) return null;
    const loginData = JSON.parse(loginDataRaw);
    return (
      loginData?.token ||
      loginData?.accessToken ||
      loginData?.jwt ||
      loginData?.data?.token ||
      loginData?.data?.accessToken ||
      loginData?.Details?.token ||
      null
    );
  } catch (_err) {
    return null;
  }
};

const hasStoredLoginState = () => {
  const token = getStoredToken();
  if (token) return true;
  const loginData = localStorage.getItem("loginData");
  const userProfile = localStorage.getItem("userProfile");
  return !!(loginData && userProfile);
};

const getStoredProfile = () => {
  try {
    return JSON.parse(localStorage.getItem("userProfile") || "null");
  } catch (_err) {
    return null;
  }
};

const getStoredRole = () => {
  const explicitRole = localStorage.getItem("authRole");
  if (explicitRole) return explicitRole;
  const profile = getStoredProfile();
  const userType = profile?.userType || profile?.user_type;
  if (userType === "Super_Admin") return "admin";
  if (userType === "HotelPartner") return "hotel";
  if (userType === "Agent") return "agent";
  if (userType === "Airline") return "airline";
  if (userType === "bus_company") return "bus";
  return "admin";
};

export const AuthProvider = ({ children }) => {
  // Initialize isLoggedIn based on token existence in localStorage
  const [isLoggedIn, setIsLoggedIn] = useState(() => {
    return hasStoredLoginState();
  });
  const [session, setSession] = useState(() => {
    const token = getStoredToken();
    return { token: token || null };
  });
  const [currency, setCurrency] = useState("ILS");
  const [isInitializing, setIsInitializing] = useState(true); // Track initialization

  const [userProfile, setUserProfile] = useState();


  const fetchUser = React.useCallback(async () => {
    try {
      const token = getStoredToken();
      if (!token) {
        setIsInitializing(false);
        return;
      }
      if (!localStorage.getItem("token")) {
        localStorage.setItem("token", token);
      }

      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };

      const cachedUser = getStoredProfile();
      const role = getStoredRole();
      const id = cachedUser?._id || cachedUser?.id || cachedUser?.user_id || cachedUser?.bus_company_id || cachedUser?.airline_id;

      // Only super-admin endpoint is reliable for profile refresh right now.
      // Hotel and Agent sessions keep cached profile to avoid invalid-role API calls.
      if ((role !== "admin" && role !== "airline" && role !== "bus") || !id) {
        if (cachedUser) {
          setUserProfile(cachedUser);
          setIsLoggedIn(true);
          setSession({ token: token });
        }
        setIsInitializing(false);
        return;
      }

      const response = await axios.get(
        apiPath.baseURL + `${apiPath.getSuperAdmin}/${id}`,
        {
          headers,
        }
      );

      if (response.data.success || response.data.status) {
        const freshUser =
          response.data.data ||
          response.data.details?.[0] ||
          response.data.Details?.[0] ||
          cachedUser;
        setUserProfile(freshUser);
        localStorage.setItem("userProfile", JSON.stringify(freshUser));
        setIsLoggedIn(true);
        setSession({ token: token });
      }
    } catch (error) {
      console.log("Fetch user error:", error);
      // If token is invalid, clear everything
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem("token");
        localStorage.removeItem("userProfile");
        localStorage.removeItem("authRole");
        setIsLoggedIn(false);
        setSession({ token: null });
        setUserProfile(null);
      }
    } finally {
      setIsInitializing(false);
    }
  }, []);

  useEffect(() => {
    const token = getStoredToken();
    // Check if we have cached user profile first
    const cachedUser = localStorage.getItem("userProfile");
    const loginData = localStorage.getItem("loginData");

    // Session-based fallback (no token based auth)
    if (!token && cachedUser && loginData) {
      try {
        const user = JSON.parse(cachedUser);
        setUserProfile(user);
        setIsLoggedIn(true);
        setSession({ token: null });
      } catch (_error) {
        localStorage.removeItem("userProfile");
        setIsLoggedIn(false);
      } finally {
        setIsInitializing(false);
      }
      return;
    }

    if (!token) {
      setIsInitializing(false);
      return;
    }
    if (!localStorage.getItem("token")) {
      localStorage.setItem("token", token);
    }

    if (cachedUser) {
      try {
        const user = JSON.parse(cachedUser);
        setUserProfile(user);
        setIsLoggedIn(true);
        setSession({ token: token });
        setIsInitializing(false);
        // Fetch fresh data in background (only once)
        fetchUser();
      } catch (error) {
        console.log("Error parsing cached user:", error);
        localStorage.removeItem("userProfile");
        setIsInitializing(false);
      }
    } else {
      // No cached user, fetch from API
      setIsLoggedIn(true);
      setSession({ token: token });
      // Wait for fetchUser to complete before setting isInitializing to false
      fetchUser().finally(() => {
        setIsInitializing(false);
      });
    }
  }, [fetchUser]);

  useEffect(() => {
    if (userProfile?.country_id?.currency) {
      setCurrency(userProfile?.country_id?.currency);
    }
  }, [userProfile]);

  // Real-time session status checking
  // useEffect(() => {
  //   const token = localStorage.getItem("token");
  //   if (!token || !isLoggedIn) return;

  //   // Check session status every 5 seconds
  //   const checkSessionInterval = setInterval(async () => {
  //     try {
  //       const headers = {
  //         Authorization: `Bearer ${token}`,
  //         "Content-Type": "application/json",
  //       };

  //       const response = await axios.get(apiPath.baseURL + apiPath.checkSessionStatus, {
  //         headers,
  //       });

  //       if (response.data.status && !response.data.valid) {
  //         // Session terminated, logout immediately
  //         console.log("Session terminated, logging out...");
  //         clearInterval(checkSessionInterval);
  //         localStorage.removeItem("token");
  //         localStorage.removeItem("userProfile");
  //         setIsLoggedIn(false);
  //         setSession({ token: null });
  //         setUserProfile();
  //         ShowToast("Your session has been terminated", Severty.ERROR);
  //         window.location.href = "/login";
  //       }
  //     } catch (error) {
  //       // If 401, session is invalid
  //       if (error.response?.status === 401) {
  //         clearInterval(checkSessionInterval);
  //         localStorage.removeItem("token");
  //         localStorage.removeItem("userProfile");
  //         setIsLoggedIn(false);
  //         setSession({ token: null });
  //         setUserProfile();
  //         ShowToast("Session expired", Severty.ERROR);
  //         window.location.href = "/login";
  //       }
  //     }
  //   }, 5000); // Check every 5 seconds

  //   return () => clearInterval(checkSessionInterval);
  // }, [isLoggedIn]);


  const logout = async () => {
    try {
      const roleBeforeLogout = localStorage.getItem("authRole");
      
      // Selectively remove session-related items
      localStorage.removeItem("loginData");
      localStorage.removeItem("token");
      localStorage.removeItem("userProfile");
      localStorage.removeItem("authRole");
      
      setIsLoggedIn(false);
      setUserProfile();
      ShowToast("Logout Successfully", Severty.SUCCESS);
      window.location.href = roleBeforeLogout === "admin" ? "/admin/login" : "/login";
    } catch (error) {
      console.log("Logout API error:", error);
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      const token = getStoredToken();
      if (!token) return { success: false, message: "Unauthorized" };

      const role = getStoredRole();
      let url;

      if (role === "agent" && userProfile) {
        // Use agent-specific endpoint
        const agentId = userProfile.agent_id || userProfile._id;
        console.log("Agent ID for change password:", agentId);
        url = apiPath.agentChangePassword(agentId);
      } else {
        // Use general change password endpoint
        url = apiPath.baseURL + apiPath.changePassword;
      }
      console.log("Change password URL:", url);

      // Use the exact field names expected by the backend API
      const payload = {
        current_password: currentPassword,
        new_password: newPassword,
        confirm_password: newPassword
      };
      console.log("Change password payload:", payload);

      const response = await axios.post(
        url,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success || response.data.status) {
        ShowToast("Password changed successfully", Severty.SUCCESS);
        return { success: true, message: response.data.message || "Password changed successfully" };
      } else {
        ShowToast(response.data.message || "Failed to change password", Severty.ERROR);
        return { success: false, message: response.data.message || "Failed to change password" };
      }
    } catch (error) {
      console.error("Change password error:", error);
      const errorMessage = error.response?.data?.message || "Failed to change password";
      ShowToast(errorMessage, Severty.ERROR);
      return { success: false, message: errorMessage };
    }
  };



  const getAgentHotelsDetails = React.useCallback(async () => {
    try {
      const token = getStoredToken();
      if (!token || !userProfile) return { success: false, message: "Unauthorized" };

      const agentId = userProfile.agent_id || userProfile._id;

      const response = await axios.get(
        apiPath.agentHotelsDetails(agentId),
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("getAgentHotelsDetails error:", error);
      return { success: false, error };
    }
  }, [userProfile]);

  const getAgentBookingsEnquires = React.useCallback(async () => {
    try {
      const token = getStoredToken();
      if (!token) return { success: false, message: "Unauthorized" };

      const response = await axios.get(
        apiPath.getAgentBookingsEnquires(),
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("getAgentBookingsEnquires error:", error);
      return { success: false, error };
    }
  }, []);

  const manager = React.useMemo(() => {
    if (!userProfile) return null;
    const role = getStoredRole();
    if (role === "hotel" || userProfile.userType === "HotelPartner" || userProfile.user_type === "HotelPartner") {
      return userProfile;
    }
    return null;
  }, [userProfile]);

  return (
    <AuthContext.Provider
      value={{
        isLoggedIn,
        setIsLoggedIn,
        session,
        setSession,
        userProfile,
        setUserProfile,
        manager,
        logout,
        fetchUser,
        getAgentHotelsDetails,
        getAgentBookingsEnquires,
        changePassword,
        currency,
        isInitializing, // Expose initialization state
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => {
  return useContext(AuthContext);
};

export const useAuth = useAuthContext;
